import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search,
  Eye,
  Download,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Store,
  Filter
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'sonner';
import OrderViewDialog from '../../components/admin/OrderViewDialog';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOrders();
    fetchVendors();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/orders`);
      const ordersWithId = response.data.map(o => ({
        ...o,
        id: o.id || o._id
      }));
      setOrders(ordersWithId);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/vendors`);
      setVendors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const getVendorName = (vendorId) => {
    if (!Array.isArray(vendors)) return 'Unknown Vendor';
    const vendor = vendors.find(v => (v.id || v._id) === vendorId);
    return vendor?.business_name || 'Unknown Vendor';
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowViewDialog(true);
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, trackingNumber = '') => {
    try {
      setUpdatingOrder(orderId);
      await axiosInstance.put(
        `${API_URL}/api/admin/orders/${orderId}/status`,
        null,
        {
          params: {
            status: newStatus,
            ...(trackingNumber && { tracking_number: trackingNumber })
          }
        }
      );
      
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, order_status: newStatus, status: newStatus }
          : order
      ));
      
      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchQuery || 
      (order.id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.customer_phone || '').includes(searchQuery) ||
      (order.items || []).some(item => (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()));
    
    const orderStatus = order.order_status || order.status || 'pending';
    const matchesStatus = statusFilter === 'all' || orderStatus === statusFilter;
    
    const matchesVendor = vendorFilter === 'all' || order.vendor_id === vendorFilter;
    
    return matchesSearch && matchesStatus && matchesVendor;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      packed: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-cyan-100 text-cyan-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const orderCounts = {
    all: orders.length,
    pending: orders.filter(o => (o.order_status || o.status) === 'pending').length,
    processing: orders.filter(o => (o.order_status || o.status) === 'processing').length,
    shipped: orders.filter(o => (o.order_status || o.status) === 'shipped').length,
    delivered: orders.filter(o => (o.order_status || o.status) === 'delivered').length,
    cancelled: orders.filter(o => (o.order_status || o.status) === 'cancelled').length
  };

  // Group orders by vendor for summary
  const vendorOrderCounts = Array.isArray(vendors) ? vendors.reduce((acc, vendor) => {
    const vendorId = vendor.id || vendor._id;
    acc[vendorId] = orders.filter(o => o.vendor_id === vendorId).length;
    return acc;
  }, {}) : {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Orders Management</h1>
              <p className="text-gray-600 mt-2">
                Manage and track all customer orders by vendor
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" data-testid="export-orders-button">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search orders by ID, customer, or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="orders-search-input"
            />
          </div>
          
          {/* Vendor Filter */}
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="w-56">
              <Store className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors ({orders.length})</SelectItem>
              {Array.isArray(vendors) && vendors.map((vendor) => {
                const vendorId = vendor.id || vendor._id;
                return (
                  <SelectItem key={vendorId} value={vendorId}>
                    {vendor.business_name} ({vendorOrderCounts[vendorId] || 0})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders ({orderCounts.all})</SelectItem>
              <SelectItem value="pending">Pending ({orderCounts.pending})</SelectItem>
              <SelectItem value="processing">Processing ({orderCounts.processing})</SelectItem>
              <SelectItem value="shipped">Shipped ({orderCounts.shipped})</SelectItem>
              <SelectItem value="delivered">Delivered ({orderCounts.delivered})</SelectItem>
              <SelectItem value="cancelled">Cancelled ({orderCounts.cancelled})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{orderCounts.all}</p>
              <p className="text-sm text-gray-500">Total Orders</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('pending')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{orderCounts.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('processing')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{orderCounts.processing}</p>
              <p className="text-sm text-gray-500">Processing</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('shipped')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{orderCounts.shipped}</p>
              <p className="text-sm text-gray-500">Shipped</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('delivered')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{orderCounts.delivered}</p>
              <p className="text-sm text-gray-500">Delivered</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('cancelled')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{orderCounts.cancelled}</p>
              <p className="text-sm text-gray-500">Cancelled</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Orders ({filteredOrders.length})</span>
              {vendorFilter !== 'all' && (
                <Badge className="bg-green-100 text-green-800">
                  <Store className="w-3 h-3 mr-1" />
                  {getVendorName(vendorFilter)}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Package className="w-6 h-6 text-gray-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">
                              Order #{(order.id || '').slice(-8).toUpperCase()}
                            </p>
                            <Badge className={getStatusColor(order.order_status || order.status || 'pending')}>
                              {order.order_status || order.status || 'pending'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          {/* Vendor Badge */}
                          <div className="mt-1">
                            <Badge variant="outline" className="text-xs">
                              <Store className="w-3 h-3 mr-1" />
                              {getVendorName(order.vendor_id)}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-8 lg:gap-12">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Customer</p>
                          <p className="font-medium text-gray-900">
                            {order.customer_name || order.guest_name || 'Guest'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {order.customer_phone || order.guest_phone || order.shipping_address?.phone || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Payment</p>
                          <Badge className={
                            order.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }>
                            {order.payment_status === 'paid' ? 'Paid' : order.payment_method || 'COD'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Items</p>
                          <p className="text-gray-900">{order.items?.length || 0} item(s)</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">
                            {formatPrice(order.total || order.total_amount || 0)}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOrder(order)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Status Update */}
                    <div className="mt-4 pt-4 border-t flex items-center justify-between">
                      <Select 
                        value={order.order_status || order.status || 'pending'} 
                        onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                        disabled={updatingOrder === order.id}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="packed">Packed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order View Dialog */}
      {showViewDialog && selectedOrder && (
        <OrderViewDialog
          order={selectedOrder}
          isOpen={showViewDialog}
          onClose={() => {
            setShowViewDialog(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
}
